import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'nofee_cookie_consent';

// Routes where legal acceptance has already happened at signup (or the page
// is a funnel/checkout step) — do not show the banner there since it can
// overlap primary CTAs and degrade conversion.
const HIDDEN_PREFIXES = [
  '/onboarding',
  '/complete-subscription',
  '/checkout',
  '/payment-success',
  '/customer/auth/magic',
  '/admin',
];

/**
 * GDPR/CCPA-compliant cookie consent banner.
 * Three options:
 *  - Accept all (analytics + non-essential)
 *  - Reject non-essential (essential only)
 *  - Manage preferences (links to Cookie Policy)
 *
 * Stores user choice in localStorage so we don't ask again.
 */
const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) setShow(true);
    } catch {
      // localStorage unavailable — don't block UI
    }
  }, []);

  const setChoice = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice: value, at: new Date().toISOString() })
      );
    } catch {}
    setShow(false);
  };

  if (!show) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-5 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="max-w-5xl mx-auto rounded-2xl border border-gold-500/30 bg-black/95 backdrop-blur-md shadow-2xl shadow-gold-500/10 pointer-events-auto overflow-hidden">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4 p-5">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-gold-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm sm:text-base font-bold text-white mb-1">
                We use cookies to make NoFee work
              </h3>
              <p className="text-xs sm:text-sm text-white/60 leading-relaxed">
                Essential cookies are required for login, ordering, and payments. We also use optional analytics &
                error-monitoring cookies (Sentry) to improve the service. You can change your mind any time on our{' '}
                <a href="/cookies" className="text-gold-400 hover:text-gold-300 underline font-medium">
                  Cookie Policy
                </a>{' '}
                page.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:gap-3 lg:flex-row flex-row-reverse">
            <button
              onClick={() => setChoice('essential_only')}
              data-testid="cookie-reject-button"
              className="px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-xl transition-colors"
            >
              Essential only
            </button>
            <button
              onClick={() => setChoice('accept_all')}
              data-testid="cookie-accept-button"
              className="px-5 py-2.5 text-sm font-bold bg-gold-500 hover:bg-gold-400 text-black rounded-xl transition-colors hover:shadow-lg hover:shadow-gold-500/30"
            >
              Accept all
            </button>
            <button
              onClick={() => setChoice('dismissed')}
              aria-label="Close"
              className="hidden lg:flex w-9 h-9 items-center justify-center rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
              data-testid="cookie-close-button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
